/**
 * lib/themeVariants.ts
 * ---------------------
 * What: Theme token definitions and helpers to produce light/dark/blueNeon
 * palettes used by the UI.
 * Why: Keeps styling concerns declarative and centralised.
 */
export type ThemeVersion = 'v1' | 'v2' | 'v3';
export type LightThemeVersion = 'v3';

export interface ThemeColors {
  background: string;
  card: string;
  text: string;
  secondaryText: string;
  accent: string;
  border: string;
  inputBackground: string;
  inputBorder: string;
  modalOverlay: string;
  modalBackground: string;
  buttonText: string;
  buttonBackground: string;
  selected: string;
  selectedText: string;
  error: string;
  success: string;
}

// ────────────────────────────────────────────────────────────
// Light theme variants (only v3 is supported now)
// ────────────────────────────────────────────────────────────

const lightVariants: Record<LightThemeVersion, ThemeColors> = {
  v3: {
    background: '#F6F9FF', // whisper blue
    card: '#FFFFFF',
    text: '#202428',
    secondaryText: '#6B7280',
    accent: '#009CFF', // unchanged accent blue
    border: '#CCE2FF', // neutral-blue border
    inputBackground: '#EEF4FF', // very light blue input bg
    inputBorder: '#CCE2FF',
    modalOverlay: 'rgba(0,0,0,0.25)',
    modalBackground: 'rgba(255,255,255,0.94)',
    buttonText: '#FFFFFF',
    buttonBackground: '#009CFF',
    selected: '#009CFF',
    selectedText: '#FFFFFF',
    error: '#E04535',
    success: '#22C45F',
  },
};

export const getLightTheme = (_version: LightThemeVersion = 'v3'): ThemeColors =>
  lightVariants.v3;

// ────────────────────────────────────────────────────────────
// Dark theme variants
// ────────────────────────────────────────────────────────────

const darkVariants: Record<ThemeVersion, ThemeColors> = {
  v1: {
    background: '#1a1a1a',
    card: '#2a2a2a',
    text: '#fff',
    secondaryText: '#999',
    accent: '#007AFF',
    border: '#333',
    inputBackground: '#222',
    inputBorder: '#333',
    modalOverlay: 'rgba(0,0,0,0.5)',
    modalBackground: '#2a2a2a',
    buttonText: '#fff',
    buttonBackground: '#007AFF',
    selected: '#007AFF',
    selectedText: '#fff',
    error: '#ff3b30',
    success: '#34C759',
  },
  v2: {
    background: '#1a1a1a',
    card: '#2a2a2a',
    text: '#fff',
    secondaryText: '#999',
    accent: '#007AFF',
    border: '#333',
    inputBackground: '#222',
    inputBorder: '#333',
    modalOverlay: 'rgba(0,0,0,0.5)',
    modalBackground: '#2a2a2a',
    buttonText: '#fff',
    buttonBackground: '#007AFF',
    selected: '#007AFF',
    selectedText: '#fff',
    error: '#ff3b30',
    success: '#34C759',
  },
  v3: {
    background: '#1a1a1a',
    card: '#2a2a2a',
    text: '#fff',
    secondaryText: '#999',
    accent: '#007AFF',
    border: '#333',
    inputBackground: '#222',
    inputBorder: '#333',
    modalOverlay: 'rgba(0,0,0,0.5)',
    modalBackground: '#2a2a2a',
    buttonText: '#fff',
    buttonBackground: '#007AFF',
    selected: '#007AFF',
    selectedText: '#fff',
    error: '#ff3b30',
    success: '#34C759',
  },
};

export const getDarkTheme = (version: ThemeVersion = 'v1'): ThemeColors =>
  darkVariants[version] ?? darkVariants.v1;

// ────────────────────────────────────────────────────────────
// Blue Neon theme variants (formerly Test Theme)
// ────────────────────────────────────────────────────────────

const blueNeonVariants: Record<ThemeVersion, ThemeColors> = {
  v1: {
    background: '#13274c', // neutral-800
    card: '#1f3255', // neutral-750
    text: '#f3f4f6', // neutral-50
    secondaryText: '#d0d4db',
    accent: '#23ffff', // teal-200
    border: '#374867',
    inputBackground: '#2b3d5e',
    inputBorder: '#374867',
    modalOverlay: 'rgba(0,0,0,0.5)',
    modalBackground: '#1f3255',
    buttonText: '#13274c',
    buttonBackground: '#23ffff',
    selected: '#23ffff',
    selectedText: '#13274c',
    error: '#ff4d3c',
    success: '#00f55f',
  },
  v2: {
    background: '#13274c',
    card: '#1f3255',
    text: '#f3f4f6',
    secondaryText: '#d0d4db',
    accent: '#23ffff',
    border: '#374867',
    inputBackground: '#2b3d5e',
    inputBorder: '#374867',
    modalOverlay: 'rgba(0,0,0,0.5)',
    modalBackground: '#1f3255',
    buttonText: '#13274c',
    buttonBackground: '#23ffff',
    selected: '#23ffff',
    selectedText: '#13274c',
    error: '#ff4d3c',
    success: '#00f55f',
  },
  v3: {
    background: '#13274c',
    card: '#1f3255',
    text: '#f3f4f6',
    secondaryText: '#d0d4db',
    accent: '#23ffff',
    border: '#374867',
    inputBackground: '#2b3d5e',
    inputBorder: '#374867',
    modalOverlay: 'rgba(0,0,0,0.5)',
    modalBackground: '#1f3255',
    buttonText: '#13274c',
    buttonBackground: '#23ffff',
    selected: '#23ffff',
    selectedText: '#13274c',
    error: '#ff4d3c',
    success: '#00f55f',
  },
};

export const getBlueNeonTheme = (version: ThemeVersion = 'v1'): ThemeColors =>
  blueNeonVariants[version] ?? blueNeonVariants.v1; 